'use client'

import React from 'react'
import { Stack } from '@mantine/core'
import { MenuItemGroup, MenuSection as MenuSectionType } from '@/types/interfaces/menu'
import { PageCategory } from '@/types/enums/StoreTypes'
import { useBookVisitDetailHref } from '@/hooks/useBookVisitHref'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { BOOK_VISIT_DETAIL_HREF } from '@/lib/routes/booking'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'
import { MenuRow } from './MenuRow'
import { SectionLabel } from './SectionLabel'

interface DrillSectionProps {
    section: MenuSectionType
    onItemClick: (item: PageCategory) => void
    isLoggedIn: boolean
}

export function DrillSection({ section, onItemClick, isLoggedIn }: DrillSectionProps) {
    const isGrouped = section.menuItems.length > 0 && 'categoryTitle' in section.menuItems[0]
    const { href: prescriptionRenewalHref, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(isLoggedIn)
    const bookVisitDetailHref = useBookVisitDetailHref()

    const renderItem = (item: PageCategory) => {
        const isPrescriptionRenewal = item.href === PRESCRIPTION_RENEWAL_HREF
        const isBookVisit = item.href === BOOK_VISIT_DETAIL_HREF
        const href = isPrescriptionRenewal
            ? prescriptionRenewalHref
            : isBookVisit
              ? bookVisitDetailHref
              : item.href
        return (
            <MenuRow
                key={item.slug}
                name={item.name}
                href={href}
                hasChevron={false}
                onClick={async (event) => {
                    if (isPrescriptionRenewal) {
                        await navigateToPrescriptionRenewal(event)
                    }
                    onItemClick(item)
                }}
            />
        )
    }

    return (
        <Stack gap={16}>
            {section.title && <SectionLabel>{section.title}</SectionLabel>}
            {isGrouped ? (
                <Stack gap={28}>
                    {(section.menuItems as MenuItemGroup[]).map((group) => (
                        <Stack key={group.categoryTitle} gap={16}>
                            <SectionLabel>{group.categoryTitle}</SectionLabel>
                            {group.items.map(renderItem)}
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Stack gap={16}>{(section.menuItems as PageCategory[]).map(renderItem)}</Stack>
            )}
        </Stack>
    )
}
